import { localizedStrings } from '@core/strings'
import { ITemplateQuestion } from '@core/entities/template/TemplateQuestion'
import { ITemplateQuestionGroup } from '@core/entities/template/TemplateQuestionGroup'
import { QuestionType } from '@core/entities/template/QuestionType'
import {
  AcknowledgementQuestionRules,
  AlphaNumericQuestionRules,
  CountdownQuestionRules,
  DateQuestionRules,
  DocumentQuestionRules,
  LinkChecklistsQuestionRules,
  LinkInspectionsQuestionRules,
  LinkPermitsQuestionRules,
  LinkRegisterItemsQuestionRules,
  MultipleChoiceQuestionRules,
  NumericQuestionRules,
  PreconditionQuestionRules,
  QuizQuestionRules,
} from './templateRules'

// Data-level validation of the questions form values. Virtualized lists only
// mount the visible rows, so Controller rules cannot cover every question.
// This walks the raw values with the same rule objects the Controllers use.
// Shared by the mobile resolver and the web stage gate.

type ValidationError = {
  type: string
  message: string
}

type QuestionGroupValidationErrors = {
  conditionalQuestionGroup?: ValidationError
  data?: Array<ValidationError | undefined>
}

export type QuestionsValidationErrors = {
  questions?: Record<number, QuestionGroupValidationErrors>
}

// Shared with the UI components that render these errors.
export const QuestionAddonErrors = {
  SIGNATURE_REQUIRED: localizedStrings.requiresSignature,
  PROOF_REQUIRED: localizedStrings.requiresProof,
  ADDITIONAL_INFORMATION_REQUIRED:
    localizedStrings.requiresAdditionalInformation,
}

type QuestionValidationRule = {
  validate: (question: ITemplateQuestion) => string | true
}

export const questionValidationRules: Partial<
  Record<QuestionType, QuestionValidationRule>
> = {
  ALPHANUMERIC: AlphaNumericQuestionRules,
  MULTIPLE_CHOICE: MultipleChoiceQuestionRules,
  DATE: DateQuestionRules,
  DATE_ONLY: DateQuestionRules,
  NUMERIC: NumericQuestionRules,
  PRE_CONDITION: PreconditionQuestionRules,
  ACKNOWLEDGE_INFORMATION: AcknowledgementQuestionRules,
  DOCUMENT: DocumentQuestionRules,
  LINK_PERMITS: LinkPermitsQuestionRules,
  LINK_CHECKLISTS: LinkChecklistsQuestionRules,
  LINK_INSPECTIONS: LinkInspectionsQuestionRules,
  LINK_REGISTER_ITEMS: LinkRegisterItemsQuestionRules,
  CONFIGURE_POST_PERMIT_CHECKS_COUNTDOWN: CountdownQuestionRules,
  QUIZ_QUESTION: QuizQuestionRules,
}

export const validateGlobalAddons = (
  question: ITemplateQuestion,
): string | true => {
  const { requiresSignature, requiresProof, requiresAdditionalInformation } =
    question

  if (
    requiresSignature === 'REQUIRED' &&
    !(question.localSignature || question.signatureUrl)
  ) {
    return QuestionAddonErrors.SIGNATURE_REQUIRED
  }

  if (
    requiresProof === 'REQUIRED' &&
    !question.submissionProofs?.length &&
    !question.submissionDocuments?.length
  ) {
    return QuestionAddonErrors.PROOF_REQUIRED
  }

  if (
    requiresAdditionalInformation === 'REQUIRED' &&
    !question.questionAdditionalInformation
  ) {
    return QuestionAddonErrors.ADDITIONAL_INFORMATION_REQUIRED
  }

  return true
}

export const validateQuestion = (
  question: ITemplateQuestion,
): string | true => {
  const rules = questionValidationRules[question.questionType]
  let questionValidation: string | true = true

  if (rules?.validate) {
    questionValidation = rules.validate(question)
  }

  if (questionValidation !== true) {
    return questionValidation
  }

  return validateGlobalAddons(question)
}

// A group and a question both have these.
type HideableItem = Pick<ITemplateQuestionGroup, 'id' | 'isHidden'>

// Returns errors shaped for react-hook-form setError; null when valid.
export const validateAllQuestions = (
  questions: Array<ITemplateQuestionGroup>,
  hiddenQuestionIds?: Array<string>,
): QuestionsValidationErrors | null => {
  const errors: Partial<QuestionsValidationErrors> = {}

  // Live visibility: useQuestionVisibility evaluates the template rules
  // against the current answers, HiddenQuestionsController writes the result
  // into the form. Null when there is no live list, e.g. no rules.
  const liveHiddenIds = hiddenQuestionIds ? new Set(hiddenQuestionIds) : null

  // The live list wins. `isHidden` is the backend's result from the last
  // submission and goes stale once an answer changes visibility, so it only
  // counts when there is no live list. Same precedence as the renderers.
  const isCurrentlyHidden = ({ id, isHidden }: HideableItem) =>
    liveHiddenIds ? liveHiddenIds.has(id) : !!isHidden

  const getGroupErrors = (
    groupIndex: number,
  ): QuestionGroupValidationErrors => {
    if (!errors.questions) {
      errors.questions = {}
    }
    if (!errors.questions[groupIndex]) {
      errors.questions[groupIndex] = {}
    }
    return errors.questions[groupIndex]
  }

  questions?.forEach((group, groupIndex) => {
    if (isCurrentlyHidden(group)) {
      return
    }

    // Optional lead-in text field of the group, required when present.
    if (group.conditionalQuestionGroup) {
      const submissionText =
        group.conditionalQuestionGroup.submissionText?.trim()

      if (!submissionText) {
        const groupErrors = getGroupErrors(groupIndex)
        groupErrors.conditionalQuestionGroup = {
          type: 'required',
          message: 'This field is required',
        }
      }
    }

    group.data?.forEach((question, questionIndex) => {
      if (isCurrentlyHidden(question)) {
        return
      }

      const validationResult = validateQuestion(question)
      if (validationResult !== true) {
        const groupErrors = getGroupErrors(groupIndex)
        if (!groupErrors.data) {
          groupErrors.data = []
        }
        groupErrors.data[questionIndex] = {
          type: 'validate',
          message: validationResult,
        }
      }
    })
  })

  return errors.questions ? errors : null
}
